import { Box, Grid, Stack, Typography, useTheme } from '@mui/material';
import { useAppSelector } from 'hooks/redux.hooks';
import { ClientPaginatedTable } from 'components/table';
import * as TableConfig from './tablesConfig';
import { selectTranslations } from 'state/reducers/translation.reducers';
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { customerAccountDetailsTypes, customerActivityTypes } from 'constants/customer-account.constants';
import { generalSelectionEnum } from './tables.types';
import { SelectList } from 'components/SelectList';
import { CalendarPickerInput } from 'components/FormCreator/inputs/CalendarPickerInput';
import { pagesEnum } from 'constants/ui.constants';
import { UserPermissionType, generalStatusEnum } from 'constants/global.constants';
import { pendingRequestsTypes } from 'constants/requests.constants';
import { DEFAULT_PAGE_SIZE } from 'constants/pagination.constants';

type IProps = {
	button: any;
	contacts: any[];
};

export const ContactsTable: React.FC<IProps> = ({ button, contacts }: IProps) => {
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const navigate = useNavigate();
	const { id } = useParams();
	const [filters, setFilters] = useState<Record<string, any>>({});
	const filtersPrefix = 'page.customerAccounts.tables.contacts.filters.';

	const roleOptions = [{ value: generalSelectionEnum.ALL, name: t(`${filtersPrefix}role.allSelected`) }];

	const permissionOptions = [
		{ value: generalSelectionEnum.ALL, name: t(`${filtersPrefix}permission.allSelected`) },
		{ value: UserPermissionType.Viewer, name: UserPermissionType.Viewer },
		{ value: UserPermissionType.Operationer, name: UserPermissionType.Operationer },
		{ value: UserPermissionType.Signer, name: UserPermissionType.Signer },
	];

	const contactStatusOptions = [
		{ value: generalSelectionEnum.ALL, name: t(`${filtersPrefix}status.allSelected`) },
		{ value: generalStatusEnum.APPROVED, name: t(`${filtersPrefix}status.Approved`) },
		{ value: generalStatusEnum.CANCELED, name: t(`${filtersPrefix}status.Canceled`) },
		{ value: generalStatusEnum.DECLINED, name: t(`${filtersPrefix}status.Declined`) },
		{ value: generalStatusEnum.PENDING, name: t(`${filtersPrefix}status.Pending`) },
		{ value: generalStatusEnum.DELETED, name: t(`${filtersPrefix}status.Deleted`) },
	];

	const [selectedRole, setSelectedRole] = useState<any>(roleOptions[0]);
	const [selectedPermission, setSelectedPermission] = useState<any>(permissionOptions[0]);
	const [selectedContactStatus, setContactStatus] = useState(contactStatusOptions[0]);
	const [selectedDate, setSelectedDate] = useState<any>(null);

	// Client-side filtering using useMemo
	const filteredContacts = useMemo(() => {
		let result = contacts;

		// Filter by date
		if (filters.date) {
			const filterDate = new Date(filters.date);
			filterDate.setHours(0, 0, 0, 0);
			result = result.filter((c: any) => {
				const createdAt = new Date(c.createdAt);
				createdAt.setHours(0, 0, 0, 0);
				return createdAt.getTime() === filterDate.getTime();
			});
		}

		// Filter by permission
		if (filters.permission && filters.permission !== generalSelectionEnum.ALL) {
			result = result.filter((c: any) => c.permission === filters.permission);
		}

		// Filter by status
		if (filters.status && filters.status !== generalSelectionEnum.ALL) {
			result = result.filter((c: any) => c.status === filters.status);
		}

		// Filter by role
		if (filters.role && filters.role !== generalSelectionEnum.ALL) {
			result = result.filter((c: any) => c.role === filters.role);
		}

		return result;
	}, [contacts, filters]);

	const handleFilterChange = (key: string, value: any) => {
		const newFilters = { ...filters };
		if (!value || value === generalSelectionEnum.ALL) {
			delete newFilters[key];
		} else {
			newFilters[key] = value;
		}
		setFilters(newFilters);
	};

	const handleDateChange = (val: any) => {
		setSelectedDate(val);
		handleFilterChange('date', val);
	};

	const handleRoleChange = (event: any) => {
		setSelectedRole(event.target);
		handleFilterChange('role', event.target.value);
	};

	const handlePermissionChange = (event: any) => {
		setSelectedPermission(event.target);
		handleFilterChange('permission', event.target.value);
	};

	const handleStatusChange = (event: any) => {
		setContactStatus(event.target);
		handleFilterChange('status', event.target.value);
	};

	return (
		<Stack spacing={2}>
			<Box sx={{ display: 'flex' }}>
				<Grid container spacing={3} columns={4} sx={{ alignContent: 'center' }}>
					<Grid item>
						<CalendarPickerInput
							label={t('label.date')}
							handleChange={handleDateChange}
							name="contactsCreationCalender"
							selectedDate={selectedDate}
							styleObj={{
								'& .MuiFormControl-root': {
									'& .MuiInputBase-root': { padding: '8.5px 14px', borderRadius: '5px!important' },
									'& .MuiInputLabel-root': {
										top: '50%',
										transform: 'translate(35%, -50%) scale(1)',
									},
								},
							}}
						/>
					</Grid>
					<Grid item>
						<SelectList
							options={roleOptions}
							selected={selectedRole}
							label={t(`${filtersPrefix}role.label`)}
							handleChange={handleRoleChange}
						/>
					</Grid>
					<Grid item>
						<SelectList
							options={permissionOptions}
							selected={selectedPermission}
							label={t(`${filtersPrefix}permission.label`)}
							handleChange={handlePermissionChange}
						/>
					</Grid>
					<Grid item>
						<SelectList
							options={contactStatusOptions}
							selected={selectedContactStatus}
							label={t(`${filtersPrefix}status.label`)}
							handleChange={handleStatusChange}
						/>
					</Grid>
				</Grid>
				{button}
			</Box>
			<Typography variant="subtitle" color={theme.palette.root.grey}>
				{filteredContacts.length + ' ' + t('messages.results')}
			</Typography>
			<ClientPaginatedTable
				rowAction={(row) => {
					[generalStatusEnum.APPROVED, generalStatusEnum.DELETED].includes(row.status)
						? navigate(
								`/${pagesEnum.CUSTOMER_ACCOUNTS_DETAILS}/${id}/${customerAccountDetailsTypes.CONTACTS}/${row.id}`
						  )
						: navigate(`/${pagesEnum.COMPANY_FEATURE_APPROVE}/${pendingRequestsTypes.CONTACTS}/${row.id}`);
				}}
				defaultSortBy="createdAt"
				defaultOrderBy="desc"
				data={TableConfig.tableData(filteredContacts)}
				columns={TableConfig.buildColumns(customerActivityTypes.CONTACTS, t)}
				perPage={DEFAULT_PAGE_SIZE}
			/>
		</Stack>
	);
};
